import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Check, X, Clock, Users, UserCheck, MessageCircle } from 'lucide-react';
import io from 'socket.io-client'; // ✅ Ավելացրած է
import { useTranslation } from 'react-i18next';
import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
import { localizeMessage } from '../utils/toast.js';
import './FollowRequestsPage.css';

const API_BASE_URL = `${GREENWICH_BACKEND_URL}`;
const API_URL = `${API_BASE_URL}/api`;
const SOCKET_URL = `${GREENWICH_BACKEND_URL}`;

const FollowRequestsPage = () => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [count, setCount] = useState(0);
  const [socket, setSocket] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/registration');
      return;
    }

    // Socket.io connection
    const newSocket = io(SOCKET_URL, {
      query: { token },
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      console.log('Socket connected for follow requests');
      // Join this user's personal room so server-side io.to(`user-${id}`)
      // emits for follow requests actually reach this socket.
      const currentUserId = localStorage.getItem('userId');
      if (currentUserId) {
        newSocket.emit('join-user-room', currentUserId);
      }
    });

    newSocket.on('new-follow-request', (data) => {
      console.log('New follow request received:', data);
      fetchFollowRequests();
      fetchRequestCount();
    });

    newSocket.on('follow-request-accepted', (data) => {
      console.log('Follow request accepted:', data);
      fetchFollowRequests();
      fetchRequestCount();
    });

    setSocket(newSocket);

    fetchFollowRequests();
    fetchRequestCount();

    return () => {
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, [navigate]);

  const getToken = () => {
    return localStorage.getItem('token');
  };

  const fetchRequestCount = async () => {
    try {
      const token = getToken();
      if (!token) return;

      const response = await fetch(`${API_URL}/follow/requests/count`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCount(data.count);
        }
      }
    } catch (error) {
      console.error('Error fetching request count:', error);
    }
  };

  const fetchFollowRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = getToken();
      if (!token) return;

      console.log('Fetching follow requests from:', `${API_URL}/follow/requests`);

      const response = await fetch(`${API_URL}/follow/requests`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (response.ok) {
        if (data.success) {
          console.log('Requests loaded:', data.requests?.length || 0);
          setRequests(data.requests || []);
        } else {
          setError(data.error || t('followRequestsPage.failedLoad'));
        }
      } else {
        setError(data.error || t('followRequestsPage.serverError', { status: response.status }));
      }
    } catch (error) {
      console.error('Error fetching follow requests:', error);
      setError(t('followRequestsPage.failedLoad'));
    } finally {
      setLoading(false);
    }
  };

  // ✅ FIXED - Մեկ հստակ endpoint-ով
  const handleAccept = async (requestId, fromUser) => {
    try {
      setError(null);
      const token = getToken();
      if (!token) return;

      console.log('Accepting request:', requestId, 'from user:', fromUser);

      // Փորձ 1 - Հիմնական endpoint
      const response = await fetch(`${API_URL}/follow/accept/${requestId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Accept response status:', response.status);
      const data = await response.json();
      console.log('Accept response data:', data);

      if (response.ok && data.success) {
        setSuccessMessage(t('followRequestsPage.acceptedRequest', { name: fromUser.name || fromUser.username }));

        // Update UI
        setRequests(prev => prev.filter(req => req._id !== requestId));
        if (count > 0) setCount(prev => prev - 1);

        // Send socket event
        if (socket) {
          socket.emit('follow-request-accepted', {
            requestId,
            acceptorId: fromUser._id,
            acceptorName: fromUser.name || fromUser.username
          });
        }

        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        // Փորձ 2 - Այլընտրանքային endpoint
        try {
          const altResponse = await fetch(`${API_URL}/follow/requests/${requestId}/accept`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          const altData = await altResponse.json();
          if (altResponse.ok && altData.success) {
            setSuccessMessage(t('followRequestsPage.acceptedRequest', { name: fromUser.name || fromUser.username }));
            setRequests(prev => prev.filter(req => req._id !== requestId));
            if (count > 0) setCount(prev => prev - 1);
            setTimeout(() => setSuccessMessage(null), 3000);
          } else {
            throw new Error(altData.error || data.error || t('followRequestsPage.failedAccept'));
          }
        } catch (altError) {
          throw new Error(data.error || t('followRequestsPage.failedAccept'));
        }
      }
    } catch (error) {
      console.error('Error accepting request:', error);
      setError(error.message || t('followRequestsPage.failedAccept'));
      setTimeout(() => setError(null), 3000);
    }
  };

  // ✅ FIXED - Մեկ հստակ endpoint-ով
  const handleReject = async (requestId, fromUser) => {
    try {
      setError(null);
      const token = getToken();
      if (!token) return;

      console.log('Rejecting request:', requestId, 'from user:', fromUser);

      // Փորձ 1 - Հիմնական endpoint
      const response = await fetch(`${API_URL}/follow/reject/${requestId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Reject response status:', response.status);
      const data = await response.json();
      console.log('Reject response data:', data);

      if (response.ok && data.success) {
        setSuccessMessage(t('followRequestsPage.rejectedRequest', { name: fromUser.name || fromUser.username }));

        // Update UI
        setRequests(prev => prev.filter(req => req._id !== requestId));
        if (count > 0) setCount(prev => prev - 1);

        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        // Փորձ 2 - Այլընտրանքային endpoint
        try {
          const altResponse = await fetch(`${API_URL}/follow/requests/${requestId}/reject`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          const altData = await altResponse.json();
          if (altResponse.ok && altData.success) {
            setSuccessMessage(t('followRequestsPage.rejectedRequest', { name: fromUser.name || fromUser.username }));
            setRequests(prev => prev.filter(req => req._id !== requestId));
            if (count > 0) setCount(prev => prev - 1);
            setTimeout(() => setSuccessMessage(null), 3000);
          } else {
            throw new Error(altData.error || data.error || t('followRequestsPage.failedReject'));
          }
        } catch (altError) {
          throw new Error(data.error || t('followRequestsPage.failedReject'));
        }
      }
    } catch (error) {
      console.error('Error rejecting request:', error);
      setError(error.message || t('followRequestsPage.failedReject'));
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleSendMessage = async (userId, userName) => {
    try {
      const token = getToken();
      if (!token) return;

      // Check if mutual follow
      const response = await fetch(`${API_URL}/users/${userId}/can-message`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        if (data.canMessage) {
          // Create or get conversation
          const convResponse = await fetch(`${API_URL}/conversations`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              participantIds: [userId],
              isGroup: false
            })
          });

          if (convResponse.ok) {
            const convData = await convResponse.json();
            navigate(`/messages/dm/${convData.conversation._id}`);
          }
        } else {
          setError(t('followRequestsPage.needMutualToMessage', { name: userName }));
          setTimeout(() => setError(null), 3000);
        }
      }
    } catch (error) {
      console.error('Error checking message permission:', error);
      setError(t('followRequestsPage.failedStartConversation'));
      setTimeout(() => setError(null), 3000);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return "/nkarner/default-avatar.jpg";
    if (imagePath.startsWith('http')) return imagePath;
    if (imagePath.startsWith('/')) return `${GREENWICH_BACKEND_URL}${imagePath}`;
    return "/nkarner/default-avatar.jpg";
  };

  const navigateToProfile = (userId) => {
    navigate(`/profile/${userId}`);
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('followRequestsPage.justNow');
    if (diffMins < 60) return t('followRequestsPage.minAgo', { n: diffMins });
    if (diffHours < 24) return t('followRequestsPage.hourAgo', { n: diffHours });
    if (diffDays < 7) return t('followRequestsPage.dayAgo', { n: diffDays });
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const handleRefresh = () => {
    fetchFollowRequests();
    fetchRequestCount();
  };

  if (loading) {
    return (
      <div className="follow-requests-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>{t('followRequestsPage.loadingRequests')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="follow-requests-container">
      <div className="follow-requests-header">
        <div className="header-left">
          <h2>
            <Users size={24} />
            {t('followRequestsPage.title')}
          </h2>
          {count > 0 && (
            <span className="request-count-badge">
              {t('followRequestsPage.pending', { count })}
            </span>
          )}
        </div>

        <div className="header-right">
          <span className="total-requests">{requests.length === 1 ? t('followRequestsPage.requestCount', { count: requests.length }) : t('followRequestsPage.requestCountPlural', { count: requests.length })}</span>
          <button
            className="refresh-btn"
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? t('followRequestsPage.refreshing') : t('followRequestsPage.refresh')}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message" onClick={() => setError(null)}>
          {localizeMessage(error)}
        </div>
      )}

      {successMessage && (
        <div className="success-message" onClick={() => setSuccessMessage(null)}>
          <Check size={16} />
          {successMessage}
        </div>
      )}

      {requests.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <UserCheck size={64} />
          </div>
          <h3>{t('followRequestsPage.noRequests')}</h3>
          <p>{t('followRequestsPage.noRequestsDesc')}</p>
          <button className="refresh-btn" onClick={handleRefresh}>
            {t('followRequestsPage.checkNewRequests')}
          </button>
        </div>
      ) : (
        <div className="requests-list">
          {requests.map(request => {
            if (!request.fromUser) return null;
            
            const user = request.fromUser;
            const userName = user.organizationName || 
              `${user.name || ''} ${user.surname || ''}`.trim() ||
              user.username ||
              user.email?.split('@')[0];
            
            return (
              <div key={request._id} className="request-card">
                <div className="request-user-section">
                  <div 
                    className="request-user-info"
                    onClick={() => navigateToProfile(user._id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <img
                      src={getImageUrl(user.profileImage)}
                      alt={userName}
                      className="user-avatar"
                      onError={(e) => {
                        e.target.src = "/nkarner/default-avatar.jpg";
                      }}
                    />
                    <div className="user-details">
                      <div className="user-name-row">
                        <h4 className="user-name">
                          {userName}
                        </h4>
                        {user.userType === 'organizer' && (
                          <span className="user-type-badge">
                            {t('followRequestsPage.organization')}
                          </span>
                        )}
                      </div>
                      <p className="user-username">
                        @{user.username || user.email?.split('@')[0]}
                      </p>
                      <div className="request-meta">
                        <span className="request-time">
                          <Clock size={12} />
                          {formatTimeAgo(request.createdAt)}
                        </span>
                        <span className="request-note">
                          {request.message || t('followRequestsPage.wantsToFollow')}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="request-actions">
                    <div className="action-buttons">
                      <button 
                        className="accept-btn"
                        onClick={() => handleAccept(request._id, user)}
                        disabled={loading}
                      >
                        <Check size={16} />
                        {t('followRequestsPage.accept')}
                      </button>
                      <button
                        className="reject-btn"
                        onClick={() => handleReject(request._id, user)}
                        disabled={loading}
                      >
                        <X size={16} />
                        {t('followRequestsPage.decline')}
                      </button>
                    </div>

                    <button
                      className="message-btn"
                      onClick={() => handleSendMessage(user._id, userName)}
                      title={t('followRequestsPage.sendMessage')}
                      disabled={loading}
                    >
                      <MessageCircle size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FollowRequestsPage;
